import {
  Box,
  Code,
  Flex,
  Heading,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink, useParams } from "react-router";

import { useAdminPullRequest } from "@/admin/data/useAdminPullRequest";
import { MarkdownDisplay } from "@/common/components/MarkdownDisplay";
import TimestampCell from "@/common/components/TimestampCell";
import { useSlugParam } from "@/common/hooks/useParam";
import { PullRequestStatusBadge } from "@/feature/components/PullRequestLink";
import { PullRequestLog } from "@reflag/shared/adminAPI";

export default function AdminPullRequest() {
  const { orgId } = useSlugParam("org");
  const { pullId } = useParams();
  const { data: pullRequest, isLoading } = useAdminPullRequest(orgId!, pullId!);

  if (isLoading) {
    return <Spinner />;
  }

  if (!pullRequest) {
    return <Text>Pull request not found</Text>;
  }

  return (
    <Box marginInline="auto" maxW="container.xl" paddingInline={4}>
      <Flex alignItems="center" as="header" gap={8} paddingBlock={4}>
        <Heading as="h1">
          <RouterLink to="/admin">Reflag admin</RouterLink>
        </Heading>
        <Text color="dimmed">/</Text>
        <RouterLink to={`/admin/${orgId}`}>
          <Link color="blue.500">Organization</Link>
        </RouterLink>
        <Text color="dimmed">/</Text>
        <Text>Pull Request</Text>
      </Flex>

      <Flex as="main" direction="column" gap={6}>
        <section>
          <Heading as="h2" fontSize="xl" mb={4}>
            Pull Request Details
          </Heading>
          <Table>
            <colgroup>
              <col width="200" />
            </colgroup>
            <Tbody>
              <Tr>
                <Th>Title</Th>
                <Td>{pullRequest.title}</Td>
              </Tr>
              <Tr>
                <Th>Status</Th>
                <Td>
                  <PullRequestStatusBadge status={pullRequest.status} />
                </Td>
              </Tr>
              <Tr>
                <Th>Number</Th>
                <Td>
                  {pullRequest.number ? (
                    pullRequest.url ? (
                      <Link
                        color="blue.500"
                        href={pullRequest.url}
                        textDecoration="underline"
                        isExternal
                      >
                        #{pullRequest.number}
                      </Link>
                    ) : (
                      <Text>#{pullRequest.number}</Text>
                    )
                  ) : (
                    <Text color="dimmed">No number assigned</Text>
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>URL</Th>
                <Td>
                  {pullRequest.url ? (
                    <Link
                      color="blue.500"
                      href={pullRequest.url}
                      textDecoration="underline"
                      isExternal
                    >
                      {pullRequest.url}
                    </Link>
                  ) : (
                    <Text color="dimmed">No URL available</Text>
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>Created</Th>
                <Td>
                  <TimestampCell value={pullRequest.createdAt} />
                </Td>
              </Tr>
              <Tr>
                <Th>Updated</Th>
                <Td>
                  <TimestampCell value={pullRequest.updatedAt} />
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </section>

        <section>
          <Heading as="h2" fontSize="xl" mb={4}>
            Logs
          </Heading>
          {pullRequest.logs && pullRequest.logs.length > 0 ? (
            <VStack align="stretch" spacing={3}>
              {pullRequest.logs.map((log: PullRequestLog) => (
                <Box
                  key={log.id}
                  _dark={{ bg: "gray.800" }}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  padding={4}
                >
                  <Flex
                    alignItems="start"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Text color="dimmed" fontSize="sm">
                      Log ID: {log.id}
                    </Text>
                    <TimestampCell value={log.createdAt} />
                  </Flex>
                  <MarkdownDisplay size="sm" value={log.message} />
                  {log.metadata && (
                    <Code whiteSpace="pre" width="100%">
                      {JSON.stringify(log.metadata, null, 2)}
                    </Code>
                  )}
                </Box>
              ))}
            </VStack>
          ) : (
            <Text color="dimmed">No logs available</Text>
          )}
        </section>
      </Flex>
    </Box>
  );
}
